import { gql } from '@apollo/client'

export const MY_PROVIDER_PROFILE_QUERY = gql`
  query MyProviderDashboardProfile {
    myProviderProfile {
      id
      bio
      yearsExperience
      languages
      hourlyRateCents
      averageRating
      totalReviews
      completedJobs
      onVacation
      vacationUntil
      verified
      verificationStatus
      user {
        id
        avatarUrl
      }
      serviceOfferings {
        id
        serviceType {
          id
        }
      }
      availabilitySlots {
        id
        dayOfWeek
        startTime
        endTime
      }
      serviceAreas {
        id
        postalCode
        areaName
        city
      }
    }
  }
`

export const PROVIDER_DASHBOARD_BOOKINGS_QUERY = gql`
  query ProviderDashboardBookings {
    pendingRequests: myProviderBookings(status: "pending", upcoming: true, first: 12) {
      totalCount
      nodes {
        id
        status
        scheduledDate
        startTime
        endTime
        durationMinutes
        priceCents
        customer {
          firstName
          lastName
        }
        serviceType {
          name
          nameEl
          translationKey
        }
        property {
          id
          label
          addressLine1
          city
          postalCode
        }
      }
    }
    upcomingBookings: myProviderBookings(status: "confirmed", upcoming: true, first: 12) {
      totalCount
      nodes {
        id
        status
        scheduledDate
        startTime
        endTime
        durationMinutes
        priceCents
        customer {
          firstName
          lastName
        }
        serviceType {
          name
          nameEl
          translationKey
        }
        property {
          id
          label
          addressLine1
          city
          postalCode
        }
      }
    }
    activeToday: myProviderBookings(status: "in_progress", upcoming: true, first: 6) {
      totalCount
      nodes {
        id
        status
        scheduledDate
      }
    }
    recentCompleted: myProviderBookings(status: "completed", first: 50) {
      totalCount
      nodes {
        id
        status
        scheduledDate
        startTime
        endTime
        durationMinutes
        priceCents
        customer {
          firstName
          lastName
        }
        serviceType {
          name
          nameEl
          translationKey
        }
        property {
          id
          label
          addressLine1
          city
          postalCode
        }
      }
    }
  }
`

export const ACCEPT_BOOKING_MUTATION = gql`
  mutation AcceptDashboardBooking($bookingId: ID!) {
    acceptBooking(input: { bookingId: $bookingId }) {
      booking {
        id
        status
        confirmedAt
      }
      errors
    }
  }
`

export const DECLINE_BOOKING_MUTATION = gql`
  mutation DeclineDashboardBooking($bookingId: ID!, $reason: String) {
    declineBooking(input: { bookingId: $bookingId, reason: $reason }) {
      booking {
        id
        status
        cancellationReason
      }
      errors
    }
  }
`

export const START_BOOKING_MUTATION = gql`
  mutation StartDashboardBooking($bookingId: ID!) {
    startBooking(input: { bookingId: $bookingId }) {
      booking {
        id
        status
        startedAt
      }
      errors
    }
  }
`

export const SET_VACATION_MODE_MUTATION = gql`
  mutation SetDashboardVacationMode($onVacation: Boolean!, $vacationUntil: ISO8601Date) {
    setVacationMode(input: { onVacation: $onVacation, vacationUntil: $vacationUntil }) {
      providerProfile {
        id
        onVacation
        vacationUntil
      }
      errors
    }
  }
`

export interface ProviderDashboardProfile {
  id: string
  bio: string | null
  yearsExperience: number | null
  languages: string[]
  hourlyRateCents: number | null
  averageRating: number
  totalReviews: number
  completedJobs: number
  onVacation: boolean
  vacationUntil: string | null
  verified: boolean
  verificationStatus: string
  user: {
    id: string
    avatarUrl: string | null
  }
  serviceOfferings: Array<{
    id: string
    serviceType: {
      id: string
    }
  }>
  availabilitySlots: Array<{
    id: string
    dayOfWeek: number
    startTime: string
    endTime: string
  }>
  serviceAreas: Array<{
    id: string
    postalCode: string
    areaName: string | null
    city: string
  }>
}

export interface MyProviderProfileData {
  myProviderProfile: ProviderDashboardProfile | null
}

export interface DashboardBookingUser {
  firstName: string
  lastName: string
}

export interface DashboardBookingProperty {
  id: string
  label: string
  addressLine1: string
  city: string | null
  postalCode: string
}

export interface DashboardBookingServiceType {
  name: string
  nameEl: string
  translationKey: string
}

export interface DashboardBooking {
  id: string
  status: string
  scheduledDate: string
  startTime: string
  endTime: string
  durationMinutes: number
  priceCents: number
  customer?: DashboardBookingUser
  serviceType?: DashboardBookingServiceType
  property?: DashboardBookingProperty | null
}

export interface DashboardBookingConnection {
  totalCount: number
  nodes: DashboardBooking[]
}

export interface ProviderDashboardBookingsData {
  pendingRequests: DashboardBookingConnection
  upcomingBookings: DashboardBookingConnection
  activeToday: DashboardBookingConnection
  recentCompleted: DashboardBookingConnection
}

export interface AcceptBookingData {
  acceptBooking: {
    booking: { id: string; status: string; confirmedAt: string | null } | null
    errors: string[]
  }
}

export interface AcceptBookingVariables {
  bookingId: string
}

export interface DeclineBookingData {
  declineBooking: {
    booking: { id: string; status: string; cancellationReason: string | null } | null
    errors: string[]
  }
}

export interface DeclineBookingVariables {
  bookingId: string
  reason?: string
}

export interface StartBookingData {
  startBooking: {
    booking: { id: string; status: string; startedAt: string | null } | null
    errors: string[]
  }
}

export interface StartBookingVariables {
  bookingId: string
}

export interface SetVacationModeData {
  setVacationMode: {
    providerProfile: { id: string; onVacation: boolean; vacationUntil: string | null } | null
    errors: string[]
  }
}

export interface SetVacationModeVariables {
  onVacation: boolean
  vacationUntil?: string
}
